import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Info, Star, StarOff, Users, Globe, Lock, Clock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Map internal status to user-facing labels
const STATUS_LABELS = {
  "Highly Active": "High Intent",
  "Active": "Open",
  "Selectively Looking": "Selective",
  "Passive": "Market Aware",
  "Very Passive": "Off Market"
};

// Map user-facing labels back to internal status
const INTERNAL_STATUS = {
  "High Intent": "Highly Active",
  "Open": "Active", 
  "Selective": "Selectively Looking",
  "Market Aware": "Passive",
  "Off Market": "Very Passive"
};

const SUGGESTIONS = {
  culture: {
    1: "Current culture aligns perfectly with my values",
    2: "Current culture is generally good",
    3: "Would prefer culture with slight improvements",
    4: "Current culture doesn't support my growth",
    5: "Need a more supportive environment ASAP"
  },
  compensation: {
    1: "Current compensation meets all my needs",
    2: "Current compensation is adequate",
    3: "Would need better compensation to switch",
    4: "Current compensation doesn't match market value",
    5: "Financial situation requires immediate change"
  },
  worklife: {
    1: "Current role provides ideal flexibility",
    2: "Current balance is good, but could improve",
    3: "Would need similar or better flexibility",
    4: "Current situation is affecting wellbeing",
    5: "Work-life integration is unsustainable"
  },
  location: {
    1: "Current location/setup is ideal",
    2: "Current commute is acceptable",
    3: "Would need similar or better arrangement",
    4: "Current commute/location isn't sustainable",
    5: "Need immediate change in work arrangement"
  },
  role: {
    1: "Current role aligns perfectly with my purpose",
    2: "Current role is generally satisfying",
    3: "Looking for work that better utilizes my skills",
    4: "Current role lacks purpose or challenge",
    5: "Need more meaningful work immediately"
  },
  stability: {
    1: "Current role offers excellent security",
    2: "Current position has good stability",
    3: "Seeking better long-term prospects",
    4: "Current situation feels uncertain",
    5: "Need reliable employment immediately"
  },
  skills: {
    1: "Current role offers excellent growth opportunities",
    2: "Current learning opportunities are good",
    3: "Looking for better skill advancement",
    4: "Limited growth in current position",
    5: "Need to build marketable skills quickly"
  },
  industry: {
    1: "Current industry has excellent prospects",
    2: "Current industry outlook is positive",
    3: "Interested in industries with better outlook",
    4: "Current industry facing significant challenges",
    5: "Need to transition to different field ASAP"
  },
  leadership: {
    1: "Current leadership is supportive and inspiring",
    2: "Current management is generally good",
    3: "Would prefer better recognition/support",
    4: "Current leadership doesn't support growth",
    5: "Need better management immediately"
  },
  growth: {
    1: "Current role has clear advancement path",
    2: "Current growth prospects are good",
    3: "Seeking better growth trajectory",
    4: "Limited progression in current role",
    5: "Need immediate advancement opportunities"
  }
};

// Preference weights for status calculation
const PREFERENCE_WEIGHTS = {
  culture: 1.2,
  compensation: 1.4,
  worklife: 1.4,
  location: 0.8,
  role: 1.5,
  stability: 1.2,
  skills: 1.1,
  industry: 1.0,
  leadership: 1.0,
  growth: 1.4
};

export function JobPreferencesDropdown() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [lastUpdated, setLastUpdated] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [scores, setScores] = useState({
    culture: 3,
    compensation: 3,
    worklife: 3,
    location: 3,
    role: 3,
    stability: 3,
    skills: 3,
    industry: 3,
    leadership: 3,
    growth: 3
  });
  
  const [highImpactCategories, setHighImpactCategories] = useState<string[]>(['compensation', 'role', 'growth']);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [autoInterviews, setAutoInterviews] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('job_preferences')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      
      if (data?.job_preferences) {
        setScores(data.job_preferences);
        setHighImpactCategories(data.job_preferences.highImpactCategories || ['compensation', 'role', 'growth']);
        setLastUpdated(new Date(data.job_preferences.lastUpdated || Date.now()));
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const calculateActivityLevel = () => {
    let totalWeightedScore = 0;
    let totalWeight = 0;

    // First check high impact categories for urgent needs
    const hasUrgentHighImpact = highImpactCategories.some(category => scores[category as keyof typeof scores] === 5);
    if (hasUrgentHighImpact) {
      return 0.9; // Very high activity level
    }

    // Calculate weighted average if no urgent needs
    Object.entries(scores).forEach(([key, value]) => {
      const weight = PREFERENCE_WEIGHTS[key as keyof typeof PREFERENCE_WEIGHTS];
      // Convert score to activity level (higher scores = higher activity)
      const activityScore = (value - 1) / 4; // Convert 1-5 to 0-1 scale
      totalWeightedScore += activityScore * weight;
      totalWeight += weight;
    });

    return totalWeightedScore / totalWeight;
  };

  const determineDetailedStatus = () => {
    const activityLevel = calculateActivityLevel();
    
    // Check for urgent high impact categories first
    const hasUrgentHighImpact = highImpactCategories.some(category => 
      scores[category as keyof typeof scores] === 5
    );
    
    if (hasUrgentHighImpact) {
      return activityLevel >= 0.8 ? "High Intent" : "Open";
    }
    
    if (activityLevel >= 0.8) return "High Intent";
    if (activityLevel >= 0.6) return "Open";
    if (activityLevel >= 0.4) return "Selective";
    if (activityLevel >= 0.2) return "Market Aware";
    return "Off Market";
  };

  const getStatusColor = (statusValue: string) => {
    switch(statusValue) {
      case "High Intent":
        return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
      case "Open":
        return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200";
      case "Selective":
        return "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200";
      case "Market Aware":
        return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
      case "Off Market":
        return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const savePreferences = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          job_preferences: {
            ...scores,
            highImpactCategories,
            lastUpdated: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setLastUpdated(new Date());
      setNeedsUpdate(false);
      const status = determineDetailedStatus();
      
      toast({
        title: "Preferences Updated",
        description: `Your NewLoop status is now: ${status}`
      });
      
      // Update auto interviews based on status
      setAutoInterviews(status === "High Intent" || status === "Open");
      
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to save preferences'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const detailedStatus = determineDetailedStatus();

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">NewLoop Status:</h3>
            <Badge className={`${getStatusColor(detailedStatus)} px-3 py-1`}>
              {detailedStatus}
            </Badge>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 ml-1">
                    <Info className="h-4 w-4 text-gray-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="w-64 p-0">
                  <div className="p-3">
                    <h4 className="font-medium mb-2">Status Levels:</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800">High Intent</Badge>
                        <span className="text-xs">Automatic Interviews ON</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-100 text-blue-800">Open</Badge>
                        <span className="text-xs">Automatic Interviews ON</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-indigo-100 text-indigo-800">Selective</Badge>
                        <span className="text-xs">Automatic Interviews OFF</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-gray-100 text-gray-800">Market Aware</Badge>
                        <span className="text-xs">Automatic Interviews OFF</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">Off Market</Badge>
                        <span className="text-xs">Automatic Interviews OFF</span>
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {needsUpdate && (
              <Badge variant="destructive" className="gap-1">
                <Clock className="h-3 w-3" />
                Update Needed
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Auto Interviews:</span>
              <Badge variant={autoInterviews ? "default" : "secondary"}>
                {autoInterviews ? "ON" : "OFF"}
              </Badge>
            </div>
            <Button 
              variant="ghost"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1"
            >
              {isExpanded ? "Hide Details" : "Show Details"}
            </Button>
          </div>
        </div>

        {isExpanded && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <div>
                {Object.entries(SUGGESTIONS).slice(0, 5).map(([category, suggestions]) => (
                  <div key={category} className="mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-blue-100 text-blue-800 w-7 h-7 rounded-full flex items-center justify-center font-medium">
                        {scores[category as keyof typeof scores]}
                      </div>
                      <span className="font-medium capitalize">{category}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-0 h-6 ml-auto"
                        onClick={() => {
                          if (highImpactCategories.includes(category)) {
                            setHighImpactCategories(prev => prev.filter(c => c !== category));
                          } else if (highImpactCategories.length < 3) {
                            setHighImpactCategories(prev => [...prev, category]);
                          } else {
                            toast({
                              title: "Maximum Categories Reached",
                              description: "You can select up to 3 high impact categories"
                            });
                          }
                        }}
                      >
                        {highImpactCategories.includes(category) ? 
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /> : 
                          <StarOff className="h-4 w-4 text-gray-400" />
                        }
                      </Button>
                      {highImpactCategories.includes(category) && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                          High impact
                        </span>
                      )}
                      {scores[category as keyof typeof scores] === 5 && (
                        <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                          Urgent
                        </span>
                      )}
                    </div>
                    
                    <div className="relative mb-6">
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-full bg-blue-600 rounded-full absolute right-0"
                          style={{ width: `${100 - ((scores[category as keyof typeof scores] - 1) / 4) * 100}%` }}
                        />
                      </div>
                      
                      <div 
                        className="absolute top-[-6px] w-4 h-4 bg-white border-2 border-blue-600 rounded-full cursor-pointer"
                        style={{ left: `calc(${((scores[category as keyof typeof scores] - 1) / 4) * 100}% - 8px)` }}
                      />
                      
                      <div className="absolute left-0 right-0 -bottom-6 flex justify-between text-sm text-gray-500">
                        <div className={scores[category as keyof typeof scores] === 1 ? 'text-blue-700 font-medium' : ''}>Content</div>
                        <div className={scores[category as keyof typeof scores] === 2 ? 'text-blue-700 font-medium' : ''}>Satisfied</div>
                        <div className={scores[category as keyof typeof scores] === 3 ? 'text-blue-700 font-medium' : ''}>Open</div>
                        <div className={scores[category as keyof typeof scores] === 4 ? 'text-blue-700 font-medium' : ''}>Active</div>
                        <div className={scores[category as keyof typeof scores] === 5 ? 'text-blue-700 font-medium' : ''}>Urgent</div>
                      </div>
                      
                      <input
                        type="range"
                        min={1}
                        max={5}
                        step={1}
                        value={scores[category as keyof typeof scores]}
                        onChange={(e) => setScores(prev => ({ 
                          ...prev, 
                          [category]: parseInt(e.target.value) 
                        }))}
                        className="absolute top-[-10px] w-full h-6 opacity-0 cursor-pointer"
                      />
                    </div>

                    <div className="h-14 mt-8">
                      <p className="text-sm text-gray-600">
                        {suggestions[scores[category as keyof typeof scores] as keyof typeof suggestions]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                {Object.entries(SUGGESTIONS).slice(5).map(([category, suggestions]) => (
                  <div key={category} className="mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-blue-100 text-blue-800 w-7 h-7 rounded-full flex items-center justify-center font-medium">
                        {scores[category as keyof typeof scores]}
                      </div>
                      <span className="font-medium capitalize">{category}</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="p-0 h-6 ml-auto"
                        onClick={() => {
                          if (highImpactCategories.includes(category)) {
                            setHighImpactCategories(prev => prev.filter(c => c !== category));
                          } else if (highImpactCategories.length < 3) {
                            setHighImpactCategories(prev => [...prev, category]);
                          } else {
                            toast({
                              title: "Maximum Categories Reached",
                              description: "You can select up to 3 high impact categories"
                            });
                          }
                        }}
                      >
                        {highImpactCategories.includes(category) ? 
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /> : 
                          <StarOff className="h-4 w-4 text-gray-400" />
                        }
                      </Button>
                      {highImpactCategories.includes(category) && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                          High impact
                        </span>
                      )}
                      {scores[category as keyof typeof scores] === 5 && (
                        <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                          Urgent
                        </span>
                      )}
                    </div>
                    
                    <div className="relative mb-6">
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-full bg-blue-600 rounded-full absolute right-0"
                          style={{ width: `${100 - ((scores[category as keyof typeof scores] - 1) / 4) * 100}%` }}
                        />
                      </div>
                      
                      <div 
                        className="absolute top-[-6px] w-4 h-4 bg-white border-2 border-blue-600 rounded-full cursor-pointer"
                        style={{ left: `calc(${((scores[category as keyof typeof scores] - 1) / 4) * 100}% - 8px)` }}
                      />
                      
                      <div className="absolute left-0 right-0 -bottom-6 flex justify-between text-sm text-gray-500">
                        <div className={scores[category as keyof typeof scores] === 1 ? 'text-blue-700 font-medium' : ''}>Content</div>
                        <div className={scores[category as keyof typeof scores] === 2 ? 'text-blue-700 font-medium' : ''}>Satisfied</div>
                        <div className={scores[category as keyof typeof scores] === 3 ? 'text-blue-700 font-medium' : ''}>Open</div>
                        <div className={scores[category as keyof typeof scores] === 4 ? 'text-blue-700 font-medium' : ''}>Active</div>
                        <div className={scores[category as keyof typeof scores] === 5 ? 'text-blue-700 font-medium' : ''}>Urgent</div>
                      </div>
                      
                      <input
                        type="range"
                        min={1}
                        max={5}
                        step={1}
                        value={scores[category as keyof typeof scores]}
                        onChange={(e) => setScores(prev => ({ 
                          ...prev, 
                          [category]: parseInt(e.target.value) 
                        }))}
                        className="absolute top-[-10px] w-full h-6 opacity-0 cursor-pointer"
                      />
                    </div>

                    <div className="h-14 mt-8">
                      <p className="text-sm text-gray-600">
                        {suggestions[scores[category as keyof typeof scores] as keyof typeof suggestions]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-6">
              <div>
                <span className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleDateString()}
                </span>
                <div className="text-xs text-gray-500 mt-1">
                  {highImpactCategories.length > 0 ? (
                    <>
                      High impact categories: {highImpactCategories.length}/3 selected
                    </>
                  ) : (
                    "No high impact categories selected"
                  )}
                </div>
              </div>
              <Button 
                onClick={savePreferences}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Preferences"}
              </Button>
            </div>
          </>
        )}
        
        {!isExpanded && (
          <div className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated.toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}